import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { toPng } from "html-to-image";
import { ErrorBoundary } from "react-error-boundary";
import PieChart from "components/Graph/PieChart";
import { GOAL_TRACK_PARAMETERS, GOAL_TRACK_PARAMETERS_COLORS, GOAL_TRACK_PARAMETERS_COLORS_V2, TASK_PLAN_SESSION_V2, TASK_PLAN_SESSION_COLOR_V2, TASK_PLAN_SESSION_CHECKED_COLOR_V2 } from "config/constants";
import { getGoalTrackingPercentage } from "helpers/assessment";
import ErrorComponent from "components/shared/ErrorComponent";

const TaskPlanSessionChart = forwardRef(({ trackingData, version = 1, isSessionTrail = false }, ref) => {
  const chartRef = useRef(null);

  const { series, colors, labels } = useMemo(() => {
    let series = [];
    let colors = [];
    let labels = []; 
    
    // If it's a session trail, use gray color for all slices
    if (isSessionTrail) {
      // Check if trackingData is an array of numbers (trials data) or objects (goal tracking data)
      if (Array.isArray(trackingData) && trackingData.every(item => typeof item === 'number')) {
        // This is trials data [incorrect, prompt, correct]
        const total = trackingData.reduce((sum, num) => sum + (num || 0), 0);

        if (total > 0) {
          // Map to O, P, I parameters
          const params = Object.keys(TASK_PLAN_SESSION_V2);

          params.forEach((param, index) => {
            const count = trackingData[index] || 0;
            const percentage = Math.round((count / total) * 100);

            if (percentage > 0) {
              series.push(percentage);
              labels.push(`${param} - ${TASK_PLAN_SESSION_V2[param]} (${count}/${total})`);
              colors.push("#D9D9D9"); // Gray color for all slices in session trail mode
            }
          });
        }
      } else {
        // This is goal tracking data
        Object.entries(TASK_PLAN_SESSION_V2).forEach(([param, label], index) => {
          let parameterPercentage = getGoalTrackingPercentage(param, trackingData);
          if (!parameterPercentage) return;

          series.push(parameterPercentage);
          labels.push(`${param} - ${label}`);
          colors.push("#D9D9D9"); // Gray color for all slices in session trail mode
        });
      }
    } else {
      // Normal mode - use original color logic
      // Check if trackingData is an array of numbers (trials data) or objects (goal tracking data)
      if (Array.isArray(trackingData) && trackingData.every(item => typeof item === 'number')) {
        // This is trials data [incorrect, prompt, correct]
        const total = trackingData.reduce((sum, num) => sum + (num || 0), 0);

        if (total > 0) {
          // Map to O, P, I parameters
          const params = Object.keys(TASK_PLAN_SESSION_V2);

          params.forEach((param, index) => {
            const count = trackingData[index] || 0;
            const percentage = Math.round((count / total) * 100);

            if (percentage > 0) {
              series.push(percentage);
              labels.push(`${param} - ${TASK_PLAN_SESSION_V2[param]} (${count}/${total})`);
              colors.push(TASK_PLAN_SESSION_COLOR_V2[param]);
            }
          });
        }
      } else {
        // This is goal tracking data (original behavior)
        Object.entries(TASK_PLAN_SESSION_V2).forEach(([param, label], index) => {
          let parameterPercentage = getGoalTrackingPercentage(param, trackingData);
          if (!parameterPercentage) return;

          series.push(parameterPercentage);
          labels.push(`${param} - ${label}`);
          colors.push(version === 2 ? TASK_PLAN_SESSION_COLOR_V2[param] : TASK_PLAN_SESSION_CHECKED_COLOR_V2[param]);
        });
      }
    }

    return {
      series,
      labels,
      colors,
    };
  }, [trackingData, version, isSessionTrail]);

  useImperativeHandle(ref, () => ({
    generateImage: async () => {
      if (chartRef.current) {
        try {
          const dataUrl = await toPng(chartRef.current, { pixelRatio: 2, fontEmbedCSS: "" });
          return dataUrl;
        } catch (error) {
          console.error("Error generating chart image", error);
        }
      }
    },
  }));

  return (
    <ErrorBoundary fallback={<ErrorComponent />}>
      <div ref={chartRef}>
        <PieChart series={series} colors={colors} labels={labels} />
      </div>
    </ErrorBoundary>
  );
});

export default TaskPlanSessionChart;